import React from "react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import WhatsAppFloat from "@/components/sections/WhatsAppFloat";
import { Button } from "@/components/ui/button";
import {
  Building2,
  TrendingUp,
  Users,
  ShieldCheck,
  FileSpreadsheet,
  Briefcase,
  ArrowRight,
  MessageCircle,
} from "lucide-react";
import { useLeadDialog } from "@/components/sections/LeadDialog";

const benefits = [
  {
    icon: TrendingUp,
    title: "Capital de trabajo",
    desc: "Compara opciones de financiamiento para inventario, operación y crecimiento de tu Pyme.",
  },
  {
    icon: Briefcase,
    title: "Financiamiento para inversión",
    desc: "Accede a créditos para maquinaria, equipamiento o expansión comercial.",
  },
  {
    icon: Users,
    title: "Convenios corporativos",
    desc: "Ofrece a tus colaboradores acceso a créditos de consumo a través de convenio con Bancoorp.",
  },
  {
    icon: FileSpreadsheet,
    title: "Asesoría sin costo",
    desc: "Un ejecutivo analiza tu necesidad y te conecta con la entidad financiera más adecuada.",
  },
  {
    icon: ShieldCheck,
    title: "Confidencialidad",
    desc: "Tus datos se tratan bajo la Ley N° 19.628 y solo se comparten con entidades aliadas.",
  },
  {
    icon: Building2,
    title: "Red de entidades",
    desc: "Trabajamos con bancos, cooperativas y financieras reguladas que atienden segmento empresa.",
  },
];

export default function Empresas() {
  const { open } = useLeadDialog();
  const openWA = () => open("empresas");
  return (
    <div className="min-h-screen bg-white" data-testid="empresas-page">
      <Header onCtaClick={openWA} />
      <main>
        <section className="bg-gradient-to-b from-slate-50 to-white py-16 lg:py-24 border-b border-slate-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="text-sm uppercase tracking-[0.2em] font-bold text-blue-600">
                Soluciones para empresas
              </span>
              <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
                Financiamiento para tu
                <span className="block text-blue-600">Pyme o empresa</span>
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
                Bancoorp conecta a empresas chilenas con entidades financieras
                que ofrecen créditos comerciales, capital de trabajo y convenios
                corporativos. Compara opciones sin costo.
              </p>

              <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 max-w-2xl">
                <strong>Importante:</strong> Bancoorp actúa como intermediario y
                no otorga directamente los créditos. Las condiciones dependen
                de la entidad financiera.
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  onClick={openWA}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-7 py-6 text-base font-semibold"
                  data-testid="empresas-cta"
                >
                  Contactar a un ejecutivo
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <Button
                  onClick={openWA}
                  variant="outline"
                  className="rounded-full px-7 py-6 text-base border-slate-300"
                  data-testid="empresas-cta-wa"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  WhatsApp empresas
                </Button>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-slate-200 bg-white shadow-xl p-8">
                <span className="text-xs font-bold uppercase tracking-widest text-blue-600">
                  Perfil atendido
                </span>
                <ul className="mt-5 space-y-4 text-sm">
                  <li className="flex justify-between">
                    <span className="text-slate-600">Pymes con antigüedad</span>
                    <strong className="text-slate-900">≥ 12 meses</strong>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-slate-600">Ventas anuales</span>
                    <strong className="text-slate-900">Desde UF 2.400</strong>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-slate-600">Montos referenciales</span>
                    <strong className="text-slate-900">$3M a $150M CLP</strong>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-slate-600">Plazos</span>
                    <strong className="text-slate-900">3 a 60 meses</strong>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-slate-600">CAE referencial</span>
                    <strong className="text-slate-900">Desde 12,68%</strong>
                  </li>
                </ul>
                <p className="mt-5 text-[11px] text-slate-500 leading-relaxed border-t border-slate-200 pt-4">
                  Valores referenciales. Las condiciones finales las define la
                  entidad financiera evaluadora.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 lg:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="text-sm uppercase tracking-[0.2em] font-bold text-blue-600">
                Qué te ofrecemos
              </span>
              <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
                Servicios para empresas
              </h2>
            </div>

            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {benefits.map((b, i) => (
                <div
                  key={i}
                  className="rounded-2xl bg-slate-50 border border-slate-200 p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-md hover:border-blue-200"
                  data-testid={`empresas-benefit-${i}`}
                >
                  <div className="h-10 w-10 rounded-lg bg-blue-600 text-white flex items-center justify-center mb-4">
                    <b.icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    {b.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {b.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-slate-900 text-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              ¿Tu empresa necesita financiamiento?
            </h2>
            <p className="mt-4 text-slate-300 max-w-2xl mx-auto">
              Conversemos. Un ejecutivo analizará tu caso y te presentará las
              mejores opciones de las entidades financieras aliadas.
            </p>
            <Button
              onClick={openWA}
              className="mt-8 bg-teal-400 hover:bg-teal-300 text-slate-900 font-semibold rounded-full px-7 py-6 text-base"
              data-testid="empresas-bottom-cta"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Hablar con un ejecutivo
            </Button>
          </div>
        </section>
      </main>
      <Footer onCtaClick={openWA} />
      <WhatsAppFloat />
    </div>
  );
}
